/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {createEmissionTester, updateEmissionTester as editEmissionTester} from '@ion/core/src/services/Connect';
import {EmissionTester} from '@ion/core/src/types/services';
import {QueryKey} from '../config/QueryKeys';
import {createMutationResult} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type carbMetaData = {
  isEdit: boolean;
  carbData: EmissionTester;
}

export type UseSaveEmissionTesterResult = UseDefaultQueryResult & {
  update: (carbMetaData: carbMetaData) => void;
};

const updateEmissionTester = async(carbMetaData: carbMetaData) => {
  try {
    let updated = {};
    const {isEdit, carbData} = carbMetaData;
    if (isEdit) {
      updated = await editEmissionTester(carbData);
    } else {
      updated = await createEmissionTester(carbData);
    }
    return updated;
  } catch (err) {
    error(err);
    throw new Error(err);
  }
};

/**
 * Common hook for save a Emission Tester entity.
 *
 * ```typescript
 *
 *    import useSaveEmissionTester from '@ion/query/src/hooks/';
 *
 *    const {update, isSuccess, isError} = useSaveEmissionTester();
 *
 * ```
 */
export const useSaveEmissionTester = (): UseSaveEmissionTesterResult => {
  const queryClient = useQueryClient();

  const mutation    = useMutation(updateEmissionTester, {
    onSettled : () => {
      queryClient.invalidateQueries([QueryKey.UPLOAD_EMISSION_TESTER]);
    }
  });

  const update = useCallback((carbMetaData: carbMetaData) => {
    mutation.mutate(carbMetaData);
  }, [mutation]);

  return createMutationResult(
    mutation, {update}
  ) as UseSaveEmissionTesterResult;
};

export default useSaveEmissionTester;
